import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { SignItLoaderModalComponent } from 'src/app/private/profile/integrations/sign-it/sign-it-loader-modal/sign-it-loader-modal.component';
import { ModalService } from 'src/app/ui/modal/modal.service';
import { catchError, delay, EMPTY, tap } from 'rxjs';
import { SignItApiService } from 'src/app/shared/api-repos/signit/signit.api.service';
import { UserProfileState } from 'src/app/shared/state/user-profile/state';
import { Logger } from 'src/app/shared/core/logger';
import { BusinessUsersState } from 'src/app/shared/state/business-users/business-users.state';
import { SignItOnboardingModalComponent } from 'src/app/private/profile/integrations/sign-it/sign-it-onboarding-modal/sign-it-onboarding-modal.component';
import { ESigningEnvelopeCreationModalComponent } from 'src/app/private/sale/documents/envelopes/modals/e-signing-envelope-creation-modal/e-signing-envelope-creation-modal.component';
import { ComponentConfig } from 'src/app/ui/drawer/drawer.model';
import { ModalAction, UI_TYPES } from 'src/app/ui/modal/modal.types';
import { BusinessState } from 'src/app/shared/state/business/business.state';
import { SignItEducationalModalComponent } from 'src/app/private/profile/integrations/sign-it/sign-it-educational-modal/sign-it-educational-modal.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { select } from '@ngxs/store';
import { SignItDisconnectModalComponent } from 'src/app/private/profile/integrations/sign-it/sign-it-disconnect-modal/sign-it-disconnect-modal.component';
import { ESigningProvider } from '@models/e-signing/enums';
import { BusinessDispatch } from '../../state/business';
import { ToastService } from 'src/app/ui/toast/toast.service';

@Injectable({ providedIn: 'root' })
export class SignItConnectionFlowService {
	private modalService = inject(ModalService);
	private signItApiService = inject(SignItApiService);
	private destroyRef = inject(DestroyRef);
	private toastService = inject(ToastService);
	private businessDispatch = inject(BusinessDispatch);

	private userProfileAddress_ = select(UserProfileState.getAddress);
	private isLoggedInUserAdmin_ = select(BusinessUsersState.isLoggedInUserAdmin);
	public isConnectedToSignIt_ = select(BusinessState.isSignITSelected);

	public isSignItConnected_ = signal(false);

	public preparePackage(packageId: string): void {
		if (this.isConnectedToSignIt_()) {
			this.createPackage(packageId);
		} else {
			this.openEducationalModal(packageId);
		}
	}

	public openEducationalModal(packageId?: string): void {
		this.modalService.openModal(SignItEducationalModalComponent, {
			...this.modalConfigBase,
			primaryAction: this.getPrimaryAction('Connect & Start Free', () =>
				this.openOnboardingModal(packageId),
			),
			secondaryAction: this.getLearnMoreAction(),
		});
	}

	public openOnboardingModal(packageId?: string): void {
		this.modalService.openModal(SignItOnboardingModalComponent, {
			...this.modalConfigBase,

			primaryAction: this.getPrimaryAction('Agree & Connect', () =>
				this.openSignItLoaderModal(packageId),
			),

			secondaryAction: this.getLearnMoreAction(),
		});
	}

	public disconnectFromSignIt(): void {
		this.modalService.openModal(SignItDisconnectModalComponent, {
			title: 'Disconnect SignIT',
			size: 'sm',
			type: UI_TYPES.DANGER,
			primaryAction: this.getPrimaryAction('Disconnect', async () => {
				this.modalService.setPrimaryActionLoading(true);
				try {
					await new Promise((resolve) => setTimeout(resolve, 1000));

					this.businessDispatch.OverwriteBusinessFields(
						'defaultSettings.eSigning.defaultProvider',
						ESigningProvider.MANUAL,
						['defaultSettings.eSigning.feeSettings.hasDisconnected', true],
					);

					this.toastService.success(
						'Your firm has been successfully disconnected from SignIT.',
						3000,
					);

					this.modalService.closeModal();
				} catch (error) {
					Logger.error(
						`SignItConnectionFlowService: Error disconnecting from SignIT: ${error}`,
					);
				} finally {
					this.modalService.setPrimaryActionLoading(false);
				}
			}),
			secondaryAction: {
				text: 'Cancel',
				action: () => this.modalService.closeModal(),
			},
		});
	}

	private createPackage(packageId: string): void {
		this.modalService.openModal(ESigningEnvelopeCreationModalComponent, {
			data: { packageId },
			...this.modalConfigBase,
		});

		Logger.info(
			`SignItConnectionFlowService: Creating package for package ID: ${packageId}`,
		);
	}

	private openSignItLoaderModal(packageId?: string): void {
		this.modalService.openModal(SignItLoaderModalComponent, {
			...this.modalConfigBase,
		});

		this.isSignItConnected_.set(false);

		this.signItApiService
			.provisionBusinessUser({
				userProfileAddress: this.userProfileAddress_(),
				termsAndConditionsAccepted: true,
			})
			.pipe(
				tap(() => this.isSignItConnected_.set(true)),
				delay(1000),
				tap(() => {
					packageId
						? this.createPackage(packageId)
						: this.modalService.closeModal();
				}),
				catchError((error) => {
					this.isSignItConnected_.set(false);
					packageId
						? this.openOnboardingModal(packageId)
						: this.modalService.closeModal();

					Logger.error(
						`SignItConnectionFlowService: Error provisioning business user${packageId ? ` for package ID: ${packageId}` : ''}: ${error}`,
					);

					return EMPTY;
				}),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe();
	}

	private readonly modalConfigBase = {
		title: 'Prepare for E-signing',
		size: 'md-sm' as ComponentConfig['size'],
		displayPill: true,
	};

	private getPrimaryAction(text: string, action: () => void): ModalAction {
		const isAdmin = this.isLoggedInUserAdmin_();

		return {
			text,
			disabled: !isAdmin,
			tooltip: !isAdmin
				? { text: 'Only Admins can connect.', position: 'above' }
				: null,
			action,
		};
	}

	private getLearnMoreAction(): ModalAction {
		return {
			text: 'Learn More',
			action: (): void =>
				void window.open('https://www.closer.ca/features/e-signing', '_blank'),
		};
	}
}
